/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { StyleSheet } from "react-native";
import Icon from "react-icons-kit";
import { check } from "react-icons-kit/feather/check";
import { x as cross } from "react-icons-kit/feather/x";

import { ExploreLink, Text } from "UpduoLibrary";
import { LocationCategory, SubLocationType } from "../types";
import {
  upduoPrimaryBlack,
  upduoFailure,
  upduoSuccess,
  upduoLightGray,
} from "config/UpduoColors";

import * as UpduoAnalytics from "config/UpduoAnalytics";

const styles = StyleSheet.create({
  cellStyle: {
    color: upduoPrimaryBlack,
  },
});

export default function LocationRow({
  index,
  location,
  locationType,
}: {
  index: number;
  location: SubLocationType;
  locationType: LocationCategory;
}) {
  const {
    quotaProgress,
    quotaTarget,
    participatingUsers,
    userCount,
    isOnTrack,
  } = location;

  let quotaCompletionString: React.ReactNode;
  if (!quotaTarget) {
    quotaCompletionString = "N/A";
  } else {
    const quotaProgressValue = quotaProgress || 0;
    const percentageComplete = Math.floor(
      quotaTarget === 0 ? 100 : (quotaProgressValue / quotaTarget) * 100
    );
    quotaCompletionString = (
      <>
        {`${quotaProgressValue}/${quotaTarget} `}
        <Text
          style={{
            color:
              isOnTrack && percentageComplete >= 100
                ? upduoSuccess
                : isOnTrack
                ? undefined
                : upduoFailure,
          }}
        >{`(${Math.min(percentageComplete, 100)}%)`}</Text>
      </>
    );
  }

  let participatingUsersString: React.ReactNode;
  if (!userCount) {
    participatingUsersString = "N/A";
  } else {
    const participatingUsersValue = participatingUsers || 0;
    const percentageParticipating = Math.floor(
      quotaTarget === 0 ? 100 : (participatingUsersValue / userCount) * 100
    );
    const participationIsOnTrack = participatingUsersValue / userCount > 0.3;
    participatingUsersString = (
      <>
        {`${participatingUsersValue}/${userCount} `}
        <Text
          style={{
            color: participationIsOnTrack ? undefined : upduoFailure,
          }}
        >{`(${Math.min(percentageParticipating, 100)}%)`}</Text>
      </>
    );
  }

  return (
    <tr>
      <td className={"ljust"}>
        <ExploreLink
          text={location.name}
          to={`/quota-progress/${locationType}/${location.id}`}
          blue={true}
          callback={() => {
            UpduoAnalytics.logEvent(`user-managerdashboard-press-location`, {
              reverseRanking: index + 1,
              locationType: locationType,
              locationId: location.id,
              locationName: location.name,
              isOnTrack: isOnTrack,
            });
          }}
        />
      </td>
      <td>
        <Text style={styles.cellStyle}>{quotaCompletionString}</Text>
      </td>
      {locationType === "user" ? null : (
        <td>
          <Text style={styles.cellStyle}>{participatingUsersString}</Text>
        </td>
      )}
      <td>
        {participatingUsersString === "N/A" ? (
          <Text style={{ color: upduoLightGray }}>{"-"}</Text>
        ) : (
          <Icon
            icon={isOnTrack ? check : cross}
            style={{ color: isOnTrack ? upduoSuccess : upduoFailure }}
          />
        )}
      </td>
    </tr>
  );
}
